import { useState, useEffect, useCallback } from "react";
import "./VideoModal.scss";

export default function VideoModal({ videos = [], currentIndex = 0, onClose }) {
  const [index, setIndex] = useState(currentIndex);
  const [copied, setCopied] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  const video = videos[index];

  // Modern Navigation Controls State Machines
  const next = useCallback(() => {
    if (index < videos.length - 1) setIndex(index + 1);
  }, [index, videos.length]);

  const prev = useCallback(() => {
    if (index > 0) setIndex(index - 1);
  }, [index]);

  // Keyboard Event Management Loop
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        next();
      } else if (e.key === "ArrowLeft") {
        prev();
      } else if (e.key === "Escape" && onClose) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [next, prev, onClose]);

  if (!video) return null;

  const isShort =
    video?.thumbnails?.high?.height > video?.thumbnails?.high?.width;

  const handleCopyLink = async () => {
    try {
      const url = `https://youtube.com{video.youtubeId}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link context string:", err);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    if (touchStart === null) return;

    const touchEnd = e.changedTouches[0].clientY;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      next();
    }
    if (distance < -minSwipeDistance) {
      prev();
    }

    setTouchStart(null);
  };

  // Resilient route backup logic block
  const subscribeUrl = video.channelId
    ? `https://youtube.com{video.channelId}?sub_confirmation=1`
    : `https://youtube.com{encodeURIComponent(video.title || 'Mini Miles Adventures')}`;

  return (
    <div className={`video-modal ${isShort ? "video-modal--short" : ""}`}>
      <div className="video-modal__backdrop" onClick={onClose} />

      <div
        className="video-modal__content"
        onTouchStart={isShort ? handleTouchStart : undefined}
        onTouchEnd={isShort ? handleTouchEnd : undefined}
        onWheel={(e) => {
          if (!isShort) return;
          if (e.deltaY > 0) next();
          if (e.deltaY < 0) prev();
        }}
      >
        <div className="video-modal__header">
          <div className="video-modal__counter">
            {isShort ? "Short" : "Video"} {index + 1} of {videos.length}
          </div>

          <div className="video-modal__header-actions">
            <button
              type="button"
              className={`video-modal__share-btn ${copied ? "copied" : ""}`}
              onClick={handleCopyLink}
              title="Copy YouTube link"
            >
              {copied ? "✓ Copied!" : "🔗 Share"}
            </button>

            <button
              type="button"
              className="video-modal__close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="video-modal__player-wrapper">
          <div className="video-modal__player">
            <iframe
              key={video.youtubeId}
              src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&playsinline=1&rel=0`}
              title={video.title}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>

        <div className="video-modal__details">
          <h3>{video.title}</h3>

          {isShort && (
            <div className="short-nav-hint">
              <span className="scroll-icon">↕</span> Swipe or Scroll to Flip
            </div>
          )}

          <div className="video-modal__engagement">
            <a
              href={`https://youtube.com{video.youtubeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button watch-youtube"
            >
              <span>Watch on YouTube</span>
            </a>
            <a
              href={subscribeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button subscribe-button"
            >
              <span>Subscribe</span>
            </a>
            <a
              href={`https://youtube.com{video.youtubeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button like-button"
            >
              <span>👍 Like / Comment</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
